import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { usePlayer } from "../../context/PlayerContext";
import { formatBytes } from "../../lib/format";
import { Card, CopyField } from "./StudioShared";
import { statusMeta } from "./helpers";

// Fenêtre d'affectation d'un titre à des playlists (PUT media/:fileId/playlists).
function AssignModal({ id, track, onClose }) {
  const { t } = useTranslation();
  const [playlists, setPlaylists] = useState(null);
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/api/radios/${id}/playlists`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setPlaylists(list);
        // Pré-coche les playlists qui contiennent déjà ce titre si l'info est présente.
        const pre = list
          .filter((p) => Array.isArray(p.media_ids) && p.media_ids.includes(track.id))
          .map((p) => p.id);
        setSelected(pre);
      })
      .catch(() => setPlaylists([]));
  }, [id, track.id]);

  const toggle = (pid) =>
    setSelected((s) => (s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/radios/${id}/media/${track.id}/playlists`, { playlistIds: selected });
      onClose();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900">{t("studio.music.assignTitle")}</h3>
        <p className="text-sm text-gray-500 mt-1 truncate">
          {track.title || (track.path ? track.path.split("/").pop() : t("studio.music.untitled"))}
        </p>
        <div className="mt-4 max-h-64 overflow-y-auto space-y-1">
          {playlists === null ? (
            <p className="text-sm text-gray-500">{t("studio.loading")}</p>
          ) : playlists.length === 0 ? (
            <p className="text-sm text-gray-500">{t("studio.music.assignEmpty")}</p>
          ) : (
            playlists.map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                <span className="text-sm text-gray-800">{p.name}</span>
              </label>
            ))
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200 text-gray-700">
            {t("studio.actions.cancel")}
          </button>
          <button
            onClick={save}
            disabled={saving || !playlists || playlists.length === 0}
            className="text-sm px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
          >
            {t("studio.actions.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MusicTab({ id, radio, plan, reloadRadio, reloadPlan }) {
  const { t } = useTranslation();
  const player = usePlayer();

  const [tracks, setTracks] = useState([]);
  const [np, setNp] = useState(null);
  const [uploads, setUploads] = useState([]); // [{key, name, pct, error}]
  const [uploadError, setUploadError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const fileInputRef = useRef(null);
  const uploadZoneRef = useRef(null);

  const scrollToUpload = () => {
    uploadZoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    fileInputRef.current?.click();
  };

  const loadTracks = useCallback(async () => {
    try {
      const res = await api.get(`/api/radios/${id}/media`);
      setTracks(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTracks([]);
    }
  }, [id]);

  useEffect(() => { loadTracks(); }, [loadTracks]);

  // Now-playing : poll toutes les 15s.
  useEffect(() => {
    let mounted = true;
    const load = () => api.get(`/api/radios/${id}/nowplaying`).then((r) => { if (mounted) setNp(r.data); }).catch(() => {});
    load();
    const tmr = setInterval(load, 15000);
    return () => { mounted = false; clearInterval(tmr); };
  }, [id]);

  const uploadFiles = useCallback(async (fileList) => {
    setUploadError("");
    const files = Array.from(fileList).filter((f) => f.type.startsWith("audio/"));
    if (files.length !== Array.from(fileList).length) setUploadError(t("studio.music.audioOnly"));
    if (files.length === 0) return;

    for (const file of files) {
      const key = `${file.name}-${Date.now()}`;
      setUploads((u) => [...u, { key, name: file.name, pct: 0, error: "" }]);
      try {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/api/radios/${id}/media`, fd, {
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setUploads((u) => u.map((it) => (it.key === key ? { ...it, pct } : it)));
          },
        });
        setUploads((u) => u.filter((it) => it.key !== key));
      } catch (err) {
        const status = err?.response?.status;
        const message = status === 413
          ? (err?.response?.data?.message || t("studio.music.storageExceeded"))
          : (err?.response?.data?.message || t("studio.music.uploadFailed"));
        setUploads((u) => u.map((it) => (it.key === key ? { ...it, error: message } : it)));
        setUploadError(message);
      }
    }
    await Promise.all([loadTracks(), reloadRadio(), reloadPlan()]);
  }, [id, loadTracks, reloadRadio, reloadPlan, t]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const deleteTrack = async (fileId) => {
    if (!window.confirm(t("studio.music.confirmDeleteTrack"))) return;
    try {
      await api.delete(`/api/radios/${id}/media/${fileId}`);
      setTracks((tr) => tr.filter((x) => x.id !== fileId));
      reloadRadio();
      reloadPlan();
    } catch { /* ignore */ }
  };

  const listen = () => {
    if (!radio?.listen_url) return;
    player?.play({
      station_uuid: "radio-" + radio.id,
      name: radio.name,
      url: radio.listen_url,
      favicon: radio.logo,
      country: radio.genre,
    });
  };

  const st = statusMeta(radio.status);
  const storageQuota = plan?.quota?.storageBytes ?? 0;
  const storageUsed = radio.storage_used_bytes ?? plan?.usage?.storageBytes ?? 0;
  const storagePct = storageQuota > 0 ? Math.min(100, (storageUsed / storageQuota) * 100) : 0;
  const nearLimit = storagePct >= 90;

  const isOnline = !!np?.is_online;
  const hasMusic = tracks.length > 0;
  const listeners = np?.listeners?.current ?? 0;
  const song = np?.now_playing?.song || {};
  const isLive = !!np?.live?.is_live;
  const npText = song.text || [song.artist, song.title].filter(Boolean).join(" — ");
  const isCurrent = player?.current?.station_uuid === "radio-" + radio.id;
  const isPlaying = isCurrent && player?.playing;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <section className="lg:col-span-2 space-y-6">
        {/* Zone d'upload */}
        <Card>
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <h3 className="font-semibold text-gray-900">{t("studio.music.title")}</h3>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md"
            >
              ＋ {t("studio.music.add")}
            </button>
          </div>

          <div
            ref={uploadZoneRef}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${dragOver ? "border-purple-500 bg-purple-50" : "border-gray-300 hover:border-purple-400"}`}
          >
            <div className="text-3xl mb-2">🎵</div>
            <p className="text-gray-700 font-medium">{t("studio.music.dropzone")}</p>
            <p className="text-sm text-gray-500 mt-1">{t("studio.music.dropzoneHint")}</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              multiple
              className="hidden"
              onChange={(e) => { if (e.target.files?.length) uploadFiles(e.target.files); e.target.value = ""; }}
            />
          </div>

          {uploadError && <p className="text-red-500 text-sm mt-3">{uploadError}</p>}

          {uploads.length > 0 && (
            <div className="mt-4 space-y-2">
              {uploads.map((u) => (
                <div key={u.key}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="truncate">{u.name}</span>
                    <span>{u.error ? u.error : `${u.pct}%`}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                    <div className={`h-full ${u.error ? "bg-red-500" : "bg-purple-500"}`} style={{ width: `${u.error ? 100 : u.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Liste des pistes */}
        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">{t("studio.music.tracks", { count: tracks.length })}</h3>
          {tracks.length === 0 ? (
            <p className="text-gray-500 text-sm">{t("studio.music.empty")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {tracks.map((tk) => (
                <li key={tk.id} className="flex items-center gap-3 py-3">
                  <div className="w-9 h-9 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center">♪</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{tk.title || (tk.path ? tk.path.split("/").pop() : t("studio.music.untitled"))}</div>
                    <div className="text-xs text-gray-500 truncate">{[tk.artist, tk.length_text].filter(Boolean).join(" • ") || "—"}</div>
                  </div>
                  <button
                    onClick={() => setAssignFor(tk)}
                    className="text-xs bg-gray-100 hover:bg-purple-100 hover:text-purple-700 text-gray-600 px-2.5 py-1.5 rounded-md whitespace-nowrap"
                  >
                    ≡ {t("studio.music.assign")}
                  </button>
                  <button
                    onClick={() => deleteTrack(tk.id)}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center"
                    aria-label={t("studio.actions.delete")}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Colonne droite : écoute, statut, stockage */}
      <aside className="space-y-6">
        <Card>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <h3 className="font-semibold text-gray-900">{t("studio.nowPlaying.title")}</h3>
            <span className={`text-xs px-2 py-1 rounded-full ${isOnline ? "bg-green-50 text-green-700" : hasMusic ? "bg-gray-100 text-gray-500" : "bg-purple-50 text-purple-700"}`}>
              {isOnline ? t("studio.nowPlaying.online") : hasMusic ? t("studio.nowPlaying.starting") : t("studio.nowPlaying.empty")}
            </span>
            {isLive && <span className="text-xs px-2 py-1 rounded-full bg-red-50 text-red-700">● {t("studio.nowPlaying.live")}</span>}
          </div>

          <div className="flex items-center gap-3">
            {song.art ? (
              <img src={song.art} alt="" className="w-14 h-14 rounded-xl object-cover bg-gray-100" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
            ) : (
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-2xl">📻</div>
            )}
            <div className="min-w-0">
              {!isOnline && !hasMusic ? (
                <button
                  type="button"
                  onClick={scrollToUpload}
                  className="text-start font-semibold text-purple-600 hover:text-purple-700"
                >
                  {t("studio.nowPlaying.emptyCta")}
                </button>
              ) : (
                <>
                  <p className="font-medium text-gray-900 truncate">{npText || (isOnline ? t("studio.nowPlaying.onAir") : t("studio.nowPlaying.warming"))}</p>
                  <p className="text-xs text-gray-500">
                    {isLive && np?.live?.streamer_name
                      ? t("studio.nowPlaying.liveBy", { name: np.live.streamer_name })
                      : t("studio.nowPlaying.listeners", { count: listeners })}
                  </p>
                </>
              )}
            </div>
          </div>

          <button
            onClick={listen}
            disabled={!radio.listen_url}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 text-white px-5 py-2.5 rounded-xl font-medium"
          >
            {isPlaying ? `❚❚ ${t("studio.nowPlaying.listening")}` : `► ${t("studio.nowPlaying.listen")}`}
          </button>
        </Card>

        <Card className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t("studio.sidebar.status")}</span>
            <span className={`text-xs px-2 py-1 rounded-full ${st.cls}`}>{t(`studio.status.${st.key}`)}</span>
          </div>
          <CopyField label={t("studio.sidebar.listenLink")} value={radio.listen_url} />
          {radio.shortcode && <CopyField label={t("studio.sidebar.shortcode")} value={radio.shortcode} />}
        </Card>

        <Card>
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span className="font-medium text-gray-900">{t("studio.sidebar.storage")}</span>
            <span>{formatBytes(storageUsed)} / {formatBytes(storageQuota)}</span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full ${nearLimit ? "bg-red-500" : "bg-blue-500"}`} style={{ width: `${storagePct}%` }} />
          </div>
          {nearLimit && (
            <p className="text-xs text-red-600 mt-2">
              {t("studio.sidebar.storageAlmostFull")} <a href="/#pricing" className="underline">{t("studio.sidebar.upgrade")}</a>
            </p>
          )}
        </Card>
      </aside>

      {assignFor && (
        <AssignModal
          id={id}
          track={assignFor}
          onClose={() => setAssignFor(null)}
        />
      )}
    </div>
  );
}
